import { useState, useEffect, useCallback, useRef } from 'react';
import { useFeatureFlag } from './useFeatureFlags';
import { useAuth } from './useAuth';
import { HybridGuidanceManager } from '@/services/hybridGuidance';
import { type Suggestion } from '@/services/guidance';

interface UseRealtimeGuidanceReturn {
  suggestions: Suggestion[];
  isLoading: boolean;
  isConnected: boolean;
  error: string | null;
  requestGuidance: () => void;
  updateContext: (
    questions: string[], 
    notes: Array<{ type: string; content: string; created_at?: string }>,
    currentStage?: 'early' | 'middle' | 'late'
  ) => void;
  streamingContent: string;
}

export const useRealtimeGuidance = (noteSpaceId?: string): UseRealtimeGuidanceReturn => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(true); // Hybrid approach is always "connected"
  const [error, setError] = useState<string | null>(null);
  const [streamingContent, setStreamingContent] = useState('');
  
  const { user } = useAuth();
  const isLiveGuidanceEnabled = useFeatureFlag('enableLiveGuidance');
  const hybridManagerRef = useRef<HybridGuidanceManager | null>(null);
  const contextRef = useRef<{
    questions: string[];
    notes: Array<{ type: string; content: string; created_at?: string }>;
    currentStage: 'early' | 'middle' | 'late';
  }>({
    questions: [],
    notes: [],
    currentStage: 'early'
  });

  // Initialize hybrid guidance manager
  useEffect(() => {
    if (!isLiveGuidanceEnabled || !user?.id) {
      return;
    }

    hybridManagerRef.current = new HybridGuidanceManager(noteSpaceId, user.id);

    return () => {
      hybridManagerRef.current?.cleanup();
      hybridManagerRef.current = null;
    };
  }, [isLiveGuidanceEnabled, noteSpaceId, user?.id]);

  const updateContext = useCallback((
    questions: string[], 
    notes: Array<{ type: string; content: string; created_at?: string }>,
    currentStage: 'early' | 'middle' | 'late' = 'middle'
  ) => {
    if (!isLiveGuidanceEnabled) {
      return;
    }

    // Store context for guidance requests
    contextRef.current = { questions, notes, currentStage };
  }, [isLiveGuidanceEnabled]);

  const requestGuidance = useCallback(async () => {
    if (!isLiveGuidanceEnabled || !hybridManagerRef.current) {
      console.log('Live guidance disabled or manager not initialized');
      return;
    }

    setError(null);
    setIsLoading(true);
    setStreamingContent('');

    try {
      const context = contextRef.current;
      console.log('Requesting guidance with hybrid approach...');
      
      const suggestions = await hybridManagerRef.current.getGuidance(
        context.questions,
        context.notes,
        context.currentStage,
        (content) => {
          // Handle streaming updates if WebSocket is used
          setStreamingContent(prev => prev + content);
        }
      );
      
      setSuggestions(suggestions);
      setStreamingContent('');
    } catch (err) {
      console.error('Hybrid guidance failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to get guidance');
    } finally {
      setIsLoading(false);
    }
  }, [isLiveGuidanceEnabled]);

  return {
    suggestions,
    isLoading,
    isConnected,
    error,
    requestGuidance,
    updateContext,
    streamingContent
  };
};